import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  FloppyDisk, 
  LockSimple,
  Warning,
  Stethoscope,
  ArrowsLeftRight,
  XCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { VitalSignsSection } from '../components/form/VitalSignsSection';
import { SignatureSection } from '../components/form/SignatureSection';
import { PatientInfoSection } from '../components/form/PatientInfoSection';
import { EventAssessmentSection } from '../components/form/EventAssessmentSection';
import { MedicalHistorySection } from '../components/form/MedicalHistorySection';
import { TransportSection } from '../components/form/TransportSection';
import { TransferCareDialog } from '../components/form/TransferCareDialog';
import { FormProgressBar } from '../components/form/FormProgressBar';
import { MedicationForm } from '../components/form/MedicationForm';
import { computeFormValidation, computeOtcValidation } from '../components/form/formValidation';
import { LOGO_1, LOGO_2, HIPAA_TEXT, RMA_TEXT, INITIAL_FORM_DATA, INITIAL_VITAL_SIGNS, INITIAL_PMH } from '../components/form/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientEditPage() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const hipaaSignatureRef = useRef(null);
  const rmaSignatureRef = useRef(null);
  const hipaaGuardianSignatureRef = useRef(null);
  const rmaGuardianSignatureRef = useRef(null);
  const providerSignatureRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [eventNames, setEventNames] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [providers, setProviders] = useState([]);
  const [formType, setFormType] = useState('acr');

  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA, form_number: '' });

  const validation = useMemo(() => 
    formType === 'medication' ? computeOtcValidation(formData) : computeFormValidation(formData),
    [formData, formType]
  );

  const fetchEventNames = useCallback(async () => {
    try {
      // Edit page loads ALL events (patient may be from a past event)
      const { data } = await axios.get(`${API_URL}/api/events`);
      setEventNames(data);
    } catch (err) {
      console.warn('Event fetch failed (non-critical):', err.message);
    }
  }, []);

  const fetchPatient = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/${id}`, {});
      
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        gender: data.gender || '',
        address: data.address || '',
        tel: data.tel || '',
        dob: data.dob || '',
        reason_for_treatment: data.reason_for_treatment || '',
        tag_color: data.tag_color || '',
        objective_assessment: data.objective_assessment || '',
        allergies: data.allergies || '',
        medication: data.medication || '',
        past_medical_history: data.past_medical_history ? {
          asthma: data.past_medical_history.asthma || false,
          diabetes: data.past_medical_history.diabetes || false,
          cardiac: data.past_medical_history.cardiac || data.past_medical_history.cardiac_hypertension || false,
          hypertension: data.past_medical_history.hypertension || data.past_medical_history.cardiac_hypertension || false,
          copd: data.past_medical_history.copd || false,
          emphysema: data.past_medical_history.emphysema || false,
          psychiatric: data.past_medical_history.psychiatric || false,
          other: data.past_medical_history.other || '',
          none: data.past_medical_history.none || false,
          unable_to_obtain: data.past_medical_history.unable_to_obtain || false
        } : { ...INITIAL_PMH },
        vital_signs_1: data.vital_signs_1 || { ...INITIAL_VITAL_SIGNS },
        vital_signs_2: data.vital_signs_2 || { ...INITIAL_VITAL_SIGNS },
        vital_signs_3: data.vital_signs_3 || { ...INITIAL_VITAL_SIGNS },
        vital_signs_4: data.vital_signs_4 || { ...INITIAL_VITAL_SIGNS },
        vitals_na: data.vitals_na || false,
        physical_assessment: data.physical_assessment || '',
        is_transported: data.is_transported || false,
        transported_by: data.transported_by || '',
        hipaa_signature: data.hipaa_signature || '',
        hipaa_signature_status: data.hipaa_signature_status || '',
        hipaa_date: data.hipaa_date || '',
        hipaa_guardian_name: data.hipaa_guardian_name || '',
        hipaa_guardian_signature: data.hipaa_guardian_signature || '',
        rma_signature: data.rma_signature || '',
        rma_signature_status: data.rma_signature_status || '',
        rma_date: data.rma_date || '',
        rma_guardian_name: data.rma_guardian_name || '',
        rma_guardian_signature: data.rma_guardian_signature || '',
        provider_signature: data.provider_signature || '',
        provider_name: data.provider_name || data.created_by_name || '',
        provider_certification: data.provider_certification || '',
        allergies_nka: data.allergies_nka || false,
        allergies_unable: data.allergies_unable || false,
        medication_none: data.medication_none || false,
        medication_unable: data.medication_unable || false,
        event_location: data.event_location || '',
        event_name: data.event_name || '',
        form_date: data.form_date || '',
        form_number: data.form_number || '',
        patient_contact_time: data.patient_contact_time || '',
        triage_center: data.triage_center || '',
        otc_medications_provided: data.otc_medications_provided || [],
        otc_other_medication: data.otc_other_medication || '',
        otc_signature: data.otc_signature || '',
        otc_date: data.otc_date || '',
        otc_agreed: data.otc_agreed || false,
      });
      setFormType(data.form_type || 'acr');
    } catch (error) {
      toast.error('Failed to load patient data');
      navigate('/patients');
    } finally {
      setFetching(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchPatient();
    fetchEventNames();
  }, [fetchPatient, fetchEventNames]);

  const fetchProviders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/providers-list`, {});
      setProviders(data.filter(p => p.id !== user?.id));
    } catch (err) {
      console.warn('Provider list fetch failed:', err.message);
    }
  }, [token, user?.id]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: { ...prev[parent], [field]: value }
    }));
  };

  const clearHipaaSignature = () => {
    hipaaSignatureRef.current?.clear();
    setFormData(prev => ({ ...prev, hipaa_signature: '' }));
  };

  const clearRmaSignature = () => {
    rmaSignatureRef.current?.clear();
    setFormData(prev => ({ ...prev, rma_signature: '' }));
  };

  const clearHipaaGuardianSignature = () => {
    hipaaGuardianSignatureRef.current?.clear();
    setFormData(prev => ({ ...prev, hipaa_guardian_signature: '' }));
  };

  const clearRmaGuardianSignature = () => {
    rmaGuardianSignatureRef.current?.clear();
    setFormData(prev => ({ ...prev, rma_guardian_signature: '' }));
  };

  const handleSaveOrSubmit = async (autoClose = false) => {
    if (!formData.first_name || !formData.last_name) {
      toast.error('Please enter patient name');
      return;
    }

    setLoading(true);

    let hipaaSignature = formData.hipaa_signature;
    let rmaSignature = formData.rma_signature;
    let hipaaGuardianSignature = formData.hipaa_guardian_signature;
    let rmaGuardianSignature = formData.rma_guardian_signature;
    
    if (hipaaSignatureRef.current && !hipaaSignatureRef.current.isEmpty()) {
      hipaaSignature = hipaaSignatureRef.current.toDataURL();
    }
    if (rmaSignatureRef.current && !rmaSignatureRef.current.isEmpty()) {
      rmaSignature = rmaSignatureRef.current.toDataURL();
    }
    if (hipaaGuardianSignatureRef.current && !hipaaGuardianSignatureRef.current.isEmpty()) {
      hipaaGuardianSignature = hipaaGuardianSignatureRef.current.toDataURL();
    }
    if (rmaGuardianSignatureRef.current && !rmaGuardianSignatureRef.current.isEmpty()) {
      rmaGuardianSignature = rmaGuardianSignatureRef.current.toDataURL();
    }

    let providerSignature = formData.provider_signature;
    if (providerSignatureRef.current && !providerSignatureRef.current.isEmpty()) {
      providerSignature = providerSignatureRef.current.toDataURL();
    }

    const { form_number, ...restFormData } = formData;
    const submitData = {
      ...restFormData,
      form_type: formType,
      hipaa_signature: hipaaSignature,
      rma_signature: rmaSignature,
      hipaa_guardian_signature: hipaaGuardianSignature,
      rma_guardian_signature: rmaGuardianSignature,
      provider_signature: providerSignature,
    };

    try {
      await axios.put(`${API_URL}/api/patients/${id}`, submitData, {});
      
      if (autoClose) {
        await axios.post(`${API_URL}/api/patients/${id}/close`, {}, {});
        toast.success('Patient record updated and form closed');
      } else {
        toast.success('Patient record saved (form stays open)');
      }
      navigate(`/patient/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update patient record');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Loading patient data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="patient-edit-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/patient/${id}`} data-testid="back-to-patient">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Back to Record
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">
                Editing: {formData.first_name} {formData.last_name}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={(e) => e.preventDefault()} className="space-y-6">

          {/* Form Type Display (read-only indicator) */}
          <Card className="border-slate-200 bg-slate-50">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">Form Type</Label>
                <span className={`inline-flex px-3 py-1.5 rounded-sm text-sm font-semibold ${formType === 'medication' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-700'}`} data-testid="edit-form-type-badge">
                  {formType === 'medication' ? 'OTC Medication' : 'ACR (Patient Care Report)'}
                </span>
              </div>
            </CardContent>
          </Card>

          {formType === 'acr' ? (
          <>
          {/* Progress Bar */}
          <FormProgressBar formData={formData} />

          {/* Transfer Care Button */}
          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => { fetchProviders(); setShowTransferDialog(true); }}
              data-testid="transfer-care-button"
            >
              <ArrowsLeftRight size={18} className="mr-2" />
              Transfer Care
            </Button>
          </div>

          <PatientInfoSection formData={formData} handleInputChange={handleInputChange} />
          <EventAssessmentSection formData={formData} handleInputChange={handleInputChange} eventNames={eventNames} showFormNumber />
          <MedicalHistorySection formData={formData} handleInputChange={handleInputChange} handleNestedChange={handleNestedChange} />

          <VitalSignsSection
            formData={formData}
            handleNestedChange={handleNestedChange}
            handleInputChange={handleInputChange}
          />

          {/* Physical Assessment */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl">Physical Assessment & Findings *</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={formData.physical_assessment}
                onChange={(e) => handleInputChange('physical_assessment', e.target.value)}
                placeholder="Enter detailed physical assessment and findings (minimum 40 characters)..."
                className="rounded-sm border-slate-300 min-h-[150px]"
                data-testid="physical-assessment-input"
              />
              <p className={`text-xs ${(formData.physical_assessment || '').length >= 40 ? 'text-green-600' : 'text-slate-400'}`}>
                {(formData.physical_assessment || '').length}/40 characters minimum
              </p>
            </CardContent>
          </Card>

          <TransportSection formData={formData} handleInputChange={handleInputChange} />

          {/* HIPAA Signature */}
          <SignatureSection
            type="hipaa"
            title="HIPAA Privacy Authorization & Disclaimer *"
            iconColor="text-green-600"
            legalText={HIPAA_TEXT}
            minorDisclaimer="I certify that I am the parent, legal guardian, or authorized representative of the minor listed below. I give permission for medical evaluation and treatment as deemed necessary by medical personnel."
            formData={formData}
            handleInputChange={handleInputChange}
            signatureRef={hipaaSignatureRef}
            guardianSignatureRef={hipaaGuardianSignatureRef}
            clearSignature={clearHipaaSignature}
            clearGuardianSignature={clearHipaaGuardianSignature}
          />

          {/* RMA Signature */}
          <SignatureSection
            type="rma"
            title="Refusal of Medical Assistance (RMA) / Transport Disclaimer"
            iconColor="text-orange-600"
            legalText={RMA_TEXT}
            minorDisclaimer="I certify that I am the parent, legal guardian, or authorized representative of the minor listed below. I am signing refusing treatment and/or transport on behalf of the child and assume all responsibility hereafter."
            formData={formData}
            handleInputChange={handleInputChange}
            signatureRef={rmaSignatureRef}
            guardianSignatureRef={rmaGuardianSignatureRef}
            clearSignature={clearRmaSignature}
            clearGuardianSignature={clearRmaGuardianSignature}
          />

          {/* Provider Signature */}
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Stethoscope size={24} className="text-teal-600" />
                Provider Signature
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Provider Name</Label>
                  <Input
                    value={formData.provider_name}
                    disabled
                    className="rounded-sm border-slate-300 bg-slate-50"
                    data-testid="provider-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Level of Certification</Label>
                  <Input
                    value={formData.provider_certification}
                    disabled
                    className="rounded-sm border-slate-300 bg-slate-50"
                    data-testid="provider-certification-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Signature</Label>
                {formData.provider_signature && (
                  <div className="mb-2">
                    <p className="text-xs text-slate-500 mb-1">Existing signature:</p>
                    <img src={formData.provider_signature} alt="Provider Signature" className="border border-slate-200 rounded bg-white max-h-20" />
                  </div>
                )}
                <div className="border border-slate-300 rounded-sm bg-white">
                  <SignatureCanvas
                    ref={providerSignatureRef}
                    canvasProps={{ className: 'w-full h-32', 'data-testid': 'provider-signature-canvas' }}
                    penColor="black"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => providerSignatureRef.current?.clear()}
                  className="text-xs text-slate-500"
                  data-testid="clear-provider-signature"
                >
                  Clear Signature
                </Button>
              </div>
            </CardContent>
          </Card>

          </>) : (
            /* Medication Form */
            <MedicationForm formData={formData} handleInputChange={handleInputChange} />
          )}

          {/* Event selection for medication type (edit mode) */}
          {formType === 'medication' && eventNames.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Event</Label>
                    <select
                      value={formData.event_name || ''}
                      onChange={(e) => handleInputChange('event_name', e.target.value)}
                      className="flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      data-testid="med-event-select"
                    >
                      <option value="">Select event</option>
                      {eventNames.map(ev => <option key={ev.id} value={ev.name}>{ev.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Form Date</Label>
                    <Input type="date" value={formData.form_date} disabled className="rounded-sm border-slate-300 bg-slate-100 cursor-not-allowed" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save & Submit Buttons */}
          <div className="flex justify-end gap-4 pb-8">
            <Link to={`/patient/${id}`}>
              <Button type="button" variant="outline" className="rounded-sm">
                Cancel
              </Button>
            </Link>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-green-300 text-green-700 hover:bg-green-50"
              disabled={loading}
              onClick={() => handleSaveOrSubmit(false)}
              data-testid="save-patient-form-button"
            >
              <FloppyDisk size={18} className="mr-2" />
              {loading ? 'Saving...' : 'Save (Keep Open)'}
            </Button>
            <Button
              type="button"
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              disabled={loading}
              onClick={() => setShowSubmitDialog(true)}
              data-testid="submit-patient-form-button"
            >
              <LockSimple size={18} className="mr-2" />
              {loading ? 'Submitting...' : 'Submit & Close'}
            </Button>
          </div>
        </form>

        {/* Submit Confirmation Dialog — shows missing fields or confirmation */}
        <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              {validation.missing.length > 0 ? (
                <>
                  <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                    <XCircle size={24} weight="fill" />
                    Cannot Submit — Missing Required Fields
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="text-sm text-slate-600 leading-relaxed">
                      <p className="mb-3">Please complete the following fields before submitting:</p>
                      <ul className="space-y-1.5" data-testid="missing-fields-list">
                        {validation.missing.map((field) => (
                          <li key={field} className="flex items-start gap-2 text-red-700">
                            <XCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-red-400" />
                            {field}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </AlertDialogDescription>
                </>
              ) : (
                <>
                  <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                    <Warning size={24} weight="fill" />
                    Notice Before Submission
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-sm text-slate-600 leading-relaxed">
                    Please review your information carefully before submitting this form. Once submitted, your responses are final and cannot be modified, edited, or undone.
                  </AlertDialogDescription>
                </>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-submit-dialog">
                {validation.missing.length > 0 ? 'Go Back & Complete' : 'Cancel'}
              </AlertDialogCancel>
              {validation.missing.length === 0 && (
                <AlertDialogAction
                  onClick={() => handleSaveOrSubmit(true)}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="confirm-submit-button"
                >
                  Submit & Close
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transfer Care Dialog */}
        <TransferCareDialog
          open={showTransferDialog}
          onOpenChange={setShowTransferDialog}
          patientId={id}
          providers={providers}
          token={token}
          onTransferComplete={() => navigate('/patients')}
        />
      </main>
    </div>
  );
}
